import { useRef, useState } from 'react'
import { Plus, Tag, Trash2 } from 'lucide-react'
import type { CrossSellingRule } from '@/lib/api/botSettings'

interface CrossSellingSectionProps {
  enabled: boolean
  closingQuestion: string
  rules: CrossSellingRule[]
  onToggleEnabled: (enabled: boolean) => void
  onChangeClosingQuestion: (val: string) => void
  onChangeRules: (rules: CrossSellingRule[]) => void
}

export default function CrossSellingSection({
  enabled,
  closingQuestion,
  rules,
  onToggleEnabled,
  onChangeClosingQuestion,
  onChangeRules,
}: CrossSellingSectionProps) {
  // Las reglas no traen id: se lleva una clave estable por posición para que
  // borrar una regla del medio no reasigne el foco ni el estado de las demás.
  const keyCounter = useRef(0)
  const newKey = () => `rule-${keyCounter.current++}`
  const [ruleKeys, setRuleKeys] = useState<string[]>(() => rules.map(newKey))
  const stableKeys = rules.map((_, index) => ruleKeys[index] ?? `rule-pending-${index}`)

  const handleAddRule = () => {
    setRuleKeys([...stableKeys, newKey()])
    onChangeRules([
      ...rules,
      {
        triggerKeyword: '',
        complementItem: '',
        offerPhrase: '',
      },
    ])
  }

  const handleUpdateRule = (index: number, patch: Partial<CrossSellingRule>) => {
    const updated = rules.map((r, i) => (i === index ? { ...r, ...patch } : r))
    onChangeRules(updated)
  }

  const handleDeleteRule = (index: number) => {
    setRuleKeys(stableKeys.filter((_, i) => i !== index))
    const updated = rules.filter((_, i) => i !== index)
    onChangeRules(updated)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-gray-900/60 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="space-y-0.5 pr-4">
          <label htmlFor="cross-selling-toggle" className="text-xs font-semibold text-gray-900 dark:text-gray-100 cursor-pointer">
            Venta Cruzada y Sugerencias de Complementos
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Si está activo, el bot sugerirá complementos relevantes configurados abajo. Si está desactivado, el bot responderá de forma directa sin sugerir ningún insumo adicional.
          </p>
        </div>
        <button
          id="cross-selling-toggle"
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => onToggleEnabled(!enabled)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${
            enabled ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
          }`}
        >
          <span className="sr-only">Activar venta cruzada</span>
          <span
            aria-hidden="true"
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              enabled ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      <div>
        <label
          htmlFor="closing-question-override"
          className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Pregunta de cierre de mostrador personalizada (opcional)
        </label>
        <input
          id="closing-question-override"
          type="text"
          value={closingQuestion}
          onChange={(e) => onChangeClosingQuestion(e.target.value)}
          className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
          placeholder="¿Anoto tu pedido para retiro en tienda o prefieres delivery?"
        />
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          Sobrescribe la pregunta final de cierre. Si se deja en blanco, se usará la pregunta predeterminada del vertical.
        </p>
      </div>

      {enabled && (
        <div className="space-y-3 pt-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-gray-100">
              <Tag className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
              <span>Reglas de complementos para el mostrador</span>
            </div>
            <button
              type="button"
              onClick={handleAddRule}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 rounded-lg transition-colors border border-indigo-200 dark:border-indigo-800"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Agregar regla</span>
            </button>
          </div>

          {rules.length === 0 ? (
            <div className="p-3.5 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 text-center text-xs text-gray-500 dark:text-gray-400">
              No hay reglas de complementos configuradas. El bot no sugerirá ningún insumo complementario hasta que agregues una regla.
            </div>
          ) : (
            <div className="space-y-2.5">
              {rules.map((rule, index) => (
                <div
                  key={stableKeys[index]}
                  className="p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700/80 space-y-2.5"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    <div>
                      <label
                        htmlFor={`trigger-${index}`}
                        className="block text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Palabra clave disparadora
                      </label>
                      <input
                        id={`trigger-${index}`}
                        type="text"
                        value={rule.triggerKeyword}
                        onChange={(e) => handleUpdateRule(index, { triggerKeyword: e.target.value })}
                        placeholder="ej: pastilla, filtro, bomba"
                        className="w-full text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`complement-${index}`}
                        className="block text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Insumo / complemento a sugerir
                      </label>
                      <input
                        id={`complement-${index}`}
                        type="text"
                        value={rule.complementItem}
                        onChange={(e) => handleUpdateRule(index, { complementItem: e.target.value })}
                        placeholder="ej: líquido de frenos DOT 4, aceite de motor"
                        className="w-full text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label
                        htmlFor={`phrase-${index}`}
                        className="block text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Frase de oferta personalizada (opcional)
                      </label>
                      <input
                        id={`phrase-${index}`}
                        type="text"
                        value={rule.offerPhrase ?? ''}
                        onChange={(e) => handleUpdateRule(index, { offerPhrase: e.target.value })}
                        placeholder="ej: ¿Te incluyo también líquido de frenos Bosch DOT 4 para el cambio?"
                        className="w-full text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div className="self-end pb-0.5">
                      <button
                        type="button"
                        onClick={() => handleDeleteRule(index)}
                        aria-label={`Eliminar regla ${index + 1}`}
                        className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
